import React from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface SalesMetrics {
  period: string;
  vehicles_sold: number;
  total_revenue?: number;
  total_cost?: number;
  gross_profit?: number;
  average_profit_per_vehicle?: number;
  profit_margin_percentage?: number;
  top_selling_makes: Array<{
    make: string;
    count: number;
    percentage: number;
  }>;
  average_days_to_sell?: number;
}

interface SalesAnalyticsCardProps {
  metrics: SalesMetrics;
  onPeriodChange: (period: string) => void;
}

const SalesAnalyticsCard: React.FC<SalesAnalyticsCardProps> = ({ metrics, onPeriodChange }) => {
  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage?: number) => {
    if (percentage === undefined || percentage === null) return 'N/A';
    return `${percentage.toFixed(1)}%`;
  };

  const getProfitColor = (profit?: number) => {
    if (!profit) return 'text-gray-500';
    return profit > 0 ? 'text-green-600' : 'text-red-600';
  };

  const periodOptions = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'ytd', label: 'Year to Date' },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <ChartBarIcon className="w-5 h-5 mr-2 text-blue-500" />
          Sales & Profitability
        </h3>
        <select
          value={metrics.period}
          onChange={(e) => onPeriodChange(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {periodOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center">
            <TruckIcon className="w-6 h-6 text-blue-500 mr-3" />
            <div>
              <p className="text-2xl font-bold text-blue-900">{metrics.vehicles_sold}</p>
              <p className="text-sm text-blue-600">Vehicles Sold</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="w-6 h-6 text-green-500 mr-3" />
            <div>
              <p className="text-lg font-bold text-green-900">
                {formatCurrency(metrics.total_revenue)}
              </p>
              <p className="text-sm text-green-600">Total Revenue</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center">
            <ArrowTrendingUpIcon className="w-6 h-6 text-purple-500 mr-3" />
            <div>
              <p className={`text-lg font-bold ${getProfitColor(metrics.gross_profit)}`}>
                {formatCurrency(metrics.gross_profit)}
              </p>
              <p className="text-sm text-purple-600">Gross Profit</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center">
            <ClockIcon className="w-6 h-6 text-yellow-500 mr-3" />
            <div>
              <p className="text-lg font-bold text-yellow-900">
                {metrics.average_days_to_sell?.toFixed(1) || 'N/A'}
              </p>
              <p className="text-sm text-yellow-600">Avg Days to Sell</p>
            </div>
          </div>
        </div>
      </div>

      {/* Profitability Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-md font-semibold text-gray-900 mb-3">Profitability</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Profit per Vehicle:</span>
              <span className={`font-medium ${getProfitColor(metrics.average_profit_per_vehicle)}`}>
                {formatCurrency(metrics.average_profit_per_vehicle)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Profit Margin:</span>
              <span className={`font-medium ${getProfitColor(metrics.profit_margin_percentage)}`}>
                {formatPercentage(metrics.profit_margin_percentage)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Cost:</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(metrics.total_cost)}
              </span>
            </div>
          </div>
        </div>

        {/* Top Selling Makes */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-md font-semibold text-gray-900 mb-3">Top Selling Makes</h4>
          {metrics.top_selling_makes.length > 0 ? (
            <div className="space-y-3">
              {metrics.top_selling_makes.slice(0, 5).map((make, index) => (
                <div key={make.make} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0
                          ? 'bg-yellow-100 text-yellow-800'
                          : index === 1
                            ? 'bg-gray-100 text-gray-800'
                            : index === 2
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{make.make}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-900">{make.count}</span>
                    <span className="text-xs text-gray-500 ml-1">({make.percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No sales data available</p>
          )}
        </div>
      </div>

      {/* Performance Indicators */}
      {metrics.vehicles_sold > 0 && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Performance Indicators:</span>
            <div className="flex space-x-4">
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  (metrics.profit_margin_percentage || 0) > 20
                    ? 'bg-green-100 text-green-800'
                    : (metrics.profit_margin_percentage || 0) > 10
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                }`}
              >
                {(metrics.profit_margin_percentage || 0) > 20
                  ? 'Strong'
                  : (metrics.profit_margin_percentage || 0) > 10
                    ? 'Moderate'
                    : 'Needs Improvement'}{' '}
                Margins
              </span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  (metrics.average_days_to_sell || 0) < 30
                    ? 'bg-green-100 text-green-800'
                    : (metrics.average_days_to_sell || 0) < 60
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                }`}
              >
                {(metrics.average_days_to_sell || 0) < 30
                  ? 'Fast'
                  : (metrics.average_days_to_sell || 0) < 60
                    ? 'Average'
                    : 'Slow'}{' '}
                Turnover
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-6 flex space-x-2">
        <button className="flex-1 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors">
          View Detailed Report
        </button>
        <button className="flex-1 px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100 transition-colors">
          Export Data
        </button>
      </div>
    </div>
  );
};

export default SalesAnalyticsCard;
